import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  DataGrid,
  GridToolbar,
} from "@mui/x-data-grid";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import {
  PDF_TABLE_STYLE,
  PDFCOLORSHEME,
} from "../../../component/constent";

import { procAllocationList } from "../../../services/reportService";
import { generateReportPDF } from "../../../utils/pdfReport";

import BreadcrumbsBar from "../../../component/common/BreadcrumbsBar";

function AllocationList() {
  const [allocationData, setAllocationData] = useState([]);
  const [loading, setLoading] = useState(false);

  // =========================================================
  // SAFE STRING
  // =========================================================
  const safeString = (value) => {
    return String(value ?? "").trim();
  };

  // =========================================================
  // LOAD ALLOCATION LIST
  // =========================================================
  const rptAllocationList = async () => {
    try {
      setLoading(true);

      const res = await procAllocationList();

      setAllocationData(res.data?.data || []);
    } catch (err) {
      console.error(
        "Failed to load allocation list:",
        err
      );

      setAllocationData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    rptAllocationList();
  }, []);

  // =========================================================
  // CLEAN DATA
  // =========================================================
  const cleanData = useMemo(() => {
    return allocationData.map((item) => ({
      Allocation: safeString(item.Allocation),

      AllocationHead: safeString(
        item.AllocationHead
      ),

      ReportCode: safeString(
        item.ReportCode
      ),
    }));
  }, [allocationData]);

  // =========================================================
  // EXCEL DOWNLOAD
  // =========================================================
  const downloadExcel = () => {
    if (!cleanData.length) {
      alert(
        "No allocation records available."
      );
      return;
    }

    const data = cleanData.map((item) => ({
      Allocation: item.Allocation,
      "Allocation Head":
        item.AllocationHead,
      "Report Code": item.ReportCode,
    }));

    const worksheet =
      XLSX.utils.json_to_sheet(data);

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Allocation List"
    );

    // Excel column widths
    worksheet["!cols"] = [
      { wch: 18 },
      { wch: 40 },
      { wch: 18 },
    ];

    const excelBuffer = XLSX.write(
      workbook,
      {
        bookType: "xlsx",
        type: "array",
      }
    );

    const file = new Blob(
      [excelBuffer],
      {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }
    );

    saveAs(
      file,
      "Allocation_List.xlsx"
    );
  };

  // =========================================================
  // PDF DOWNLOAD
  // =========================================================
  const downloadPDF = () => {
    if (!cleanData.length) {
      alert(
        "No allocation records available."
      );
      return;
    }

    // Group data by Report Code
    const groupedData =
      cleanData.reduce(
        (acc, item) => {
          const reportCode =
            item.ReportCode;

          if (!acc[reportCode]) {
            acc[reportCode] = [];
          }

          acc[reportCode].push(item);

          return acc;
        },
        {}
      );

    const rows = [];
    let srNo = 1;

    Object.keys(groupedData).forEach(
      (reportCode) => {
        // Group Header
        rows.push([
          {
            content: `Report Code : ${reportCode}`,
            colSpan: 4,
            styles: {
              fontStyle: "bold",
              halign: "left",
              fillColor:
                PDFCOLORSHEME.BACK_COLOR_GROUP,
              textColor:
                PDFCOLORSHEME.FONT_COLOR_GROUP,
              fontSize:
                PDFCOLORSHEME.FONT_SIZE_GROUP,
            },
          },
        ]);

        // Group Data
        groupedData[reportCode].forEach(
          (item) => {
            rows.push([
              srNo++,
              item.Allocation,
              item.AllocationHead,
              item.ReportCode,
            ]);
          }
        );

        // Blank row between groups
        rows.push([
          "",
          "",
          "",
          "",
        ]);
      }
    );

    generateReportPDF({
      title: "Allocation List Report",

      fileName: "Allocation_List",

      columns: [
        "S.No",
        "Allocation",
        "Allocation Head",
        "Report Code",
      ],

      rows,
    });
  };

  // =========================================================
  // DATAGRID ROWS
  // =========================================================
  const rows = useMemo(() => {
    return cleanData.map(
      (item, index) => ({
        id: index + 1,
        serialNo: index + 1,
        Allocation: item.Allocation,
        AllocationHead:
          item.AllocationHead,
        ReportCode: item.ReportCode,
      })
    );
  }, [cleanData]);

  // =========================================================
  // DATAGRID COLUMNS
  // =========================================================
  const columns = [
    {
      field: "serialNo",
      headerName: "S.No",
      width: 75,
      headerAlign: "center",
      align: "center",
    },

    {
      field: "ReportCode",
      headerName: "Report Code",
      width: 150,
    },

    {
      field: "AllocationHead",
      headerName: "Allocation Head",
      flex: 1,
      minWidth: 280,
    },

    {
      field: "Allocation",
      headerName: "Allocation Code",
      width: 170,
    },
  ];

  return (
    <Box>
      {/* =====================================================
          BREADCRUMBS
      ===================================================== */}
      <BreadcrumbsBar
        page="Allocation List"
      />

      {/* =====================================================
          PROFESSIONAL HEADER
      ===================================================== */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          mb: 3,
          p: {
            xs: 2.5,
            md: 3.5,
          },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Background */}
        <Box
          sx={{
            position: "absolute",
            right: -50,
            top: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
          }}
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2.5}
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #1976d2, #42a5f5)",
              color: "white",
              boxShadow:
                "0 8px 20px rgba(25,118,210,0.25)",
              flexShrink: 0,
            }}
          >
            <AccountTreeIcon
              sx={{
                fontSize: 34,
              }}
            />
          </Box>

          {/* Title */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                fontSize: {
                  xs: "1.65rem",
                  md: "2rem",
                },
              }}
            >
              Allocation List
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 680,
              }}
            >
              View, search and download allocation
              mappings organized by report code.
            </Typography>
          </Box>

          {/* Record Count */}
          <Box
            sx={{
              minWidth: {
                xs: "100%",
                sm: 180,
              },
              textAlign: {
                xs: "left",
                sm: "right",
              },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              Total Records
            </Typography>

            <Typography
              variant="h5"
              color="primary"
              fontWeight={800}
              sx={{
                mt: 0.2,
              }}
            >
              {cleanData.length.toLocaleString()}
            </Typography>

            <Chip
              label={
                cleanData.length
                  ? "Data Available"
                  : "No Records"
              }
              size="small"
              color={
                cleanData.length
                  ? "success"
                  : "default"
              }
              variant="outlined"
              sx={{
                mt: 0.5,
                fontWeight: 600,
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* =====================================================
          EXPORT ACTIONS
      ===================================================== */}
      <Paper
        elevation={0}
        className="no-print"
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <DownloadIcon
              color="primary"
              sx={{
                fontSize: 26,
              }}
            />

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
              >
                Export Allocation List
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Download the allocation list in Excel
                or grouped PDF format.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            p: {
              xs: 2,
              md: 2.5,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
          >
            <Button
              variant="contained"
              startIcon={
                <DownloadIcon />
              }
              onClick={downloadExcel}
              disabled={
                loading ||
                cleanData.length === 0
              }
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 180,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Download Excel
            </Button>

            <Button
              variant="contained"
              color="error"
              startIcon={
                <PictureAsPdfIcon />
              }
              onClick={downloadPDF}
              disabled={
                loading ||
                cleanData.length === 0
              }
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 180,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Download PDF
            </Button>

            <Button
              variant="outlined"
              startIcon={
                <RefreshIcon />
              }
              onClick={rptAllocationList}
              disabled={loading}
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 130,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* =====================================================
          ALLOCATION DATA TABLE
      ===================================================== */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
              >
                Allocation Records
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Search, filter and review allocation
                mappings by report code.
              </Typography>
            </Box>

            <Chip
              icon={
                <AccountTreeIcon />
              }
              label={`${cleanData.length.toLocaleString()} Records`}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 600,
              }}
            />
          </Stack>
        </Box>

        <Divider />

        {/* DataGrid */}
        <Box
          sx={{
            height: {
              xs: 520,
              md: 620,
            },
            width: "100%",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            initialState={{
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[
              10,
              25,
              50,
              100,
            ]}
            disableRowSelectionOnClick
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                  debounceMs: 500,
                },
              },
            }}
             sx={{
  border: 0,

  /* ================================
     COLUMN HEADER
     ================================ */
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#1976d2 !important",
    color: "#ffffff !important",
  },

  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#1976d2 !important",
    color: "#ffffff !important",
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#ffffff !important",
    fontWeight: 700,
  },

 

  /* Menu / filter icon */
  "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
    color: "#ffffff !important",
  },

  /* ================================
     TABLE CELLS
     ================================ */
  "& .MuiDataGrid-cell": {
    borderColor: "divider",
  },

  /* ================================
     ROW HOVER
     ================================ */
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(30, 3, 236, 0.04)",
  },

  /* ================================
     TOOLBAR
     ================================ */
  "& .MuiDataGrid-toolbarContainer": {
    padding: "12px 16px",
    borderBottom: "1px solid",
    borderColor: "divider",
  },

  /* ================================
     FOOTER
     ================================ */
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid",
    borderColor: "divider",
  },
}}
          />
        </Box>
      </Paper>
    </Box>
  );
}

export default AllocationList;

